import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DashboardData {
  userName: string;
  userEmail: string | null;
  userId: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isInstructor: boolean;
  hasInstructorRecord: boolean;
  branches: { id: string; name: string }[];
  userBranchId: string | null; // The branch assigned to the current user
}

const fetchDashboardData = async (): Promise<DashboardData | null> => {
  const { data: { session } } = await supabase.auth.getSession();
  
  if (!session) {
    return null;
  }

  // Fetch all data in parallel for speed
  const [profileResult, branchesResult, userRolesResult, rolesResult] = await Promise.all([
    supabase
      .from("profiles")
      .select("full_name, approval_status")
      .eq("id", session.user.id)
      .maybeSingle(),
    supabase
      .from("batches")
      .select("id, name")
      .in("name", ["IC Calicut", "IC Bangalore"]),
    supabase
      .from("user_roles")
      .select("branch_id")
      .eq("user_id", session.user.id)
      .not("branch_id", "is", null)
      .limit(1),
    Promise.all([
      supabase.rpc('has_role', { _user_id: session.user.id, _role: 'admin' }),
      supabase.rpc('has_role', { _user_id: session.user.id, _role: 'manager' }),
      supabase.rpc('has_role', { _user_id: session.user.id, _role: 'super_admin' }),
      supabase.rpc('has_role', { _user_id: session.user.id, _role: 'instructor' }),
    ])
  ]);

  const profile = profileResult.data;
  const branches = branchesResult.data || [];
  const userBranchId = userRolesResult.data?.[0]?.branch_id || null;
  const [hasAdminRole, hasManagerRole, hasSuperAdminRole, hasInstructorRole] = rolesResult;

  if (!profile) {
    return null;
  }

  // Check approval status
  if (profile.approval_status === "pending" || profile.approval_status === "rejected") {
    return null;
  }

  const isAdmin = Boolean(hasAdminRole.data || hasManagerRole.data || hasSuperAdminRole.data);
  const isSuperAdmin = Boolean(hasSuperAdminRole.data);
  const isInstructor = Boolean(hasInstructorRole.data);

  // Check instructor record only if user has instructor role
  let hasInstructorRecord = true;
  if (isInstructor) {
    const { data: instructorRecord } = await supabase
      .from("instructors")
      .select("id")
      .eq("user_id", session.user.id)
      .maybeSingle();
    hasInstructorRecord = !!instructorRecord;
  }

  return {
    userName: profile.full_name || "",
    userEmail: session.user.email ?? null,
    userId: session.user.id,
    isAdmin,
    isSuperAdmin,
    isInstructor,
    hasInstructorRecord,
    branches,
    userBranchId,
  };
};

export const useDashboardData = () => {
  return useQuery({
    queryKey: ["dashboard-data"],
    queryFn: fetchDashboardData,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in memory for 10 minutes
  });
};
